/**
 * FolderList Component
 * Displays folder tree with nested hierarchy, create, edit, and delete functionality
 */

import React, { useState, useEffect } from 'react';
import { apiClient, Folder, FolderCreate, FolderTreeNode } from '../services/api';
import { emailToUsername, formatDate } from '../utils/formatters';
import { useRBAC } from './ProtectedRoute';
import { useConfirmDialog } from './ConfirmDialog';
import './FolderList.css';

interface FolderListProps {
  onFolderSelect?: (folder: Folder) => void;
  parentId?: string;
  showTree?: boolean;
  hideCreateButton?: boolean;
}

export const FolderList: React.FC<FolderListProps> = ({ onFolderSelect, parentId, showTree = true, hideCreateButton = false }) => {
  const { canWrite, isAdmin } = useRBAC();
  const { confirm, dialog } = useConfirmDialog();
  const [folders, setFolders] = useState<Folder[]>([]);
  const [folderTree, setFolderTree] = useState<FolderTreeNode[]>([]);
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [editingFolderId, setEditingFolderId] = useState<string | null>(null);
  const [formData, setFormData] = useState<FolderCreate>({ name: '', description: '', icon: '', parent_id: parentId });
  const [searchTerm] = useState('');

  /**
   * Load all folders from API (flat list)
   */
  const loadFolders = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Load test_case folders, root only if no parentId
      const data = await apiClient.getFolders('test_case', !parentId);
      setFolders(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load folders');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Load folder tree structure
   */
  const loadFolderTree = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Load test_case folder tree
      const tree = await apiClient.getFolderTree(parentId, 'test_case');
      setFolderTree(tree);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load folder tree');
    } finally {
      setIsLoading(false);
    }
  };

  // Load scenarios on mount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (showTree) {
      loadFolderTree();
    } else {
      loadFolders();
    }
  }, [showTree, parentId]);

  /**
   * Toggle folder expansion
   */
  const toggleFolder = (folderId: string) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(folderId)) {
      newExpanded.delete(folderId);
    } else {
      newExpanded.add(folderId);
    }
    setExpandedFolders(newExpanded);
  };

  /**
   * Handle folder creation
   */
  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setError('Folder name is required');
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      await apiClient.createFolder({
        name: formData.name.trim(),
        description: formData.description?.trim() || undefined,
        icon: formData.icon || undefined,
        parent_id: formData.parent_id,
        folder_type: 'test_case',
      });
      setFormData({ name: '', description: '', parent_id: parentId });
      setIsCreating(false);
      // Reload folders
      if (showTree) {
        await loadFolderTree();
      } else {
        await loadFolders();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create folder');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle folder update
   */
  const handleUpdate = async (folderId: string, e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setError('Folder name is required');
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      await apiClient.updateFolder(folderId, {
        name: formData.name.trim(),
        description: formData.description?.trim() || undefined,
        icon: formData.icon || undefined,
      });
      setEditingFolderId(null);
      setFormData({ name: '', description: '', parent_id: parentId });
      // Reload folders
      if (showTree) {
        await loadFolderTree();
      } else {
        await loadFolders();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update folder');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle folder deletion
   */
  const handleDelete = async (folderId: string) => {
    const confirmed = await confirm(
      'Delete Project',
      'Are you sure you want to delete this project? This will also delete all subfolders and their contents. This action cannot be undone.'
    );
    if (!confirmed) return;

    setIsLoading(true);
    setError(null);
    try {
      await apiClient.deleteFolder(folderId);
      // Reload folders
      if (showTree) {
        await loadFolderTree();
      } else {
        await loadFolders();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete folder');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Start editing a folder
   */
  const startEdit = (folder: Folder) => {
    setEditingFolderId(folder.id);
    setFormData({
      name: folder.name,
      description: folder.description || '',
      icon: folder.icon || '',
      parent_id: folder.parent_id,
    });
    setIsCreating(false);
  };

  /**
   * Cancel editing or creating
   */
  const cancelEdit = () => {
    setEditingFolderId(null);
    setIsCreating(false);
    setFormData({ name: '', description: '', parent_id: parentId });
    setError(null);
  };

  /**
   * Start creating a new folder
   */
  const startCreate = (parentFolderId?: string) => {
    setIsCreating(true);
    setEditingFolderId(null);
    setFormData({ name: '', description: '', parent_id: parentFolderId || parentId });
    setError(null);
  };

  /**
   * Filter tree nodes based on search term
   */
  const filterTreeNodes = (nodes: FolderTreeNode[]): FolderTreeNode[] => {
    if (!searchTerm.trim()) return nodes;
    
    const search = searchTerm.toLowerCase();
    return nodes.filter(node => {
      const matchesName = node.name.toLowerCase().includes(search);
      const matchesDescription = node.description?.toLowerCase().includes(search);
      const hasMatchingChildren = node.children && filterTreeNodes(node.children).length > 0;
      
      if (matchesName || matchesDescription || hasMatchingChildren) {
        // Auto-expand nodes with matches
        if (hasMatchingChildren) {
          expandedFolders.add(node.id);
        }
        return true;
      }
      return false;
    }).map(node => ({
      ...node,
      children: node.children ? filterTreeNodes(node.children) : []
    }));
  };

  /**
   * Render a folder tree node recursively
   */
  const renderTreeNode = (node: FolderTreeNode, depth: number = 0): JSX.Element => {
    const isExpanded = expandedFolders.has(node.id);
    const hasChildren = node.children && node.children.length > 0;

    return (
      <li key={node.id} className="folder-tree-item" style={{ paddingLeft: `${depth * 1.5}rem` }}>
        {editingFolderId === node.id ? (
          <form className="folder-form inline" onSubmit={(e) => handleUpdate(node.id, e)}>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Folder name"
              disabled={isLoading}
              required
              autoFocus
            />
            <button type="submit" className="btn btn-sm btn-primary" disabled={isLoading}>
              Save
            </button>
            <button type="button" className="btn btn-sm btn-secondary" onClick={cancelEdit} disabled={isLoading}>
              Cancel
            </button>
          </form>
        ) : (
          <div className="folder-tree-node">
            <div className="folder-tree-content">
              {hasChildren && (
                <button
                  className="folder-expand-btn"
                  onClick={() => toggleFolder(node.id)}
                  aria-label={isExpanded ? 'Collapse' : 'Expand'}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" style={{ transform: isExpanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }}>
                    <path d="M6 4l4 4-4 4V4z"/>
                  </svg>
                </button>
              )}
              {!hasChildren && <span className="folder-spacer"></span>}
              <div
                className="folder-info clickable"
                onClick={() => onFolderSelect?.(node)}
                role="button"
                tabIndex={0}
              >
                <svg className="folder-icon" width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z"/>
                </svg>
                <span className="folder-name">{node.name}</span>
              </div>
            </div>
            <div className="folder-actions">
              <button
                className="btn btn-icon btn-sm"
                onClick={() => startCreate(node.id)}
                disabled={isLoading}
                title="Create subfolder"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M8 4a.5.5 0 01.5.5v3h3a.5.5 0 010 1h-3v3a.5.5 0 01-1 0v-3h-3a.5.5 0 010-1h3v-3A.5.5 0 018 4z"/>
                </svg>
              </button>
              <button
                className="btn btn-icon btn-sm"
                onClick={() => startEdit(node)}
                disabled={isLoading}
                title="Edit folder"
              >
                ✏️
              </button>
              <button
                className="btn btn-icon btn-sm btn-danger"
                onClick={() => handleDelete(node.id)}
                disabled={isLoading}
                title="Delete folder"
              >
                🗑️
              </button>
            </div>
          </div>
        )}
        {hasChildren && isExpanded && (
          <ul className="folder-tree-children">
            {sortByName(node.children).map(child => renderTreeNode(child, depth + 1))}
          </ul>
        )}
      </li>
    );
  };

  const handleIconUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setFormData(prev => ({ ...prev, icon: reader.result as string }));
    };
    reader.readAsDataURL(file);
  };

  const CARD_COLORS = [
    { bg: '#dbeafe', text: '#1d4ed8' },  // blue
    { bg: '#d1fae5', text: '#065f46' },  // emerald
    { bg: '#ede9fe', text: '#6d28d9' },  // purple
    { bg: '#ffedd5', text: '#c2410c' },  // orange
    { bg: '#ffe4e6', text: '#be123c' },  // rose
    { bg: '#fef9c3', text: '#854d0e' },  // yellow
    { bg: '#dcfce7', text: '#166534' },  // green
    { bg: '#e0f2fe', text: '#0369a1' },  // sky
  ];

  const getCardColor = (name: string) => {
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return CARD_COLORS[Math.abs(hash) % CARD_COLORS.length];
  };

  // Alphabetical, case-insensitive — same convention as FolderTree.tsx
  const sortByName = <T extends { name: string }>(list: T[]): T[] =>
    [...list].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));

  const filteredTree = sortByName(filterTreeNodes(folderTree));
  const filteredFolders = sortByName(
    searchTerm.trim()
      ? folders.filter(f =>
          f.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          f.description?.toLowerCase().includes(searchTerm.toLowerCase())
        )
      : folders
  );

  return (
    <div className="folder-list">
      {dialog}
      {/* Page header: title + subtitle + Add button */}
      <div className="projects-page-header">
        <div className="projects-page-title">
          <h2>Projects</h2>
          <p>Manage your enterprise testing workspace and project clusters.</p>
        </div>
        {!hideCreateButton && !isCreating && !editingFolderId && isAdmin && (
          <button
            className="btn btn-primary btn-add-project"
            onClick={() => startCreate()}
            disabled={isLoading}
          >
            <span className="btn-add-icon">+</span> Add New Project
          </button>
        )}
      </div>

      {error && (
        <div className="error-message" role="alert">
          {error}
        </div>
      )}

      {isCreating && (
        <form className="folder-form" onSubmit={handleCreate}>
          <h3>Create New {formData.parent_id ? 'Subfolder' : 'Project'}</h3>
          <div className="form-group">
            <label htmlFor="folder-name">Name *</label>
            <input
              id="folder-name"
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Enter folder name"
              disabled={isLoading}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="folder-description">Description</label>
            <textarea
              id="folder-description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Enter folder description (optional)"
              disabled={isLoading}
              rows={3}
            />
          </div>
          <div className="form-group">
            <label>Project Icon</label>
            <div className="icon-upload-row">
              {formData.icon && (
                <img src={formData.icon} alt="icon preview" className="icon-preview" />
              )}
              <input type="file" accept="image/*" onChange={handleIconUpload} disabled={isLoading} />
              {formData.icon && (
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setFormData(prev => ({ ...prev, icon: '' }))}>
                  Remove
                </button>
              )}
            </div>
          </div>
          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={isLoading}>
              {isLoading ? 'Creating...' : 'Create'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={cancelEdit} disabled={isLoading}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {isLoading && folderTree.length === 0 && folders.length === 0 && !isCreating ? (
        <div className="loading">Loading folders...</div>
      ) : showTree ? (
        <div className="folder-tree">
          {filteredTree.length === 0 && !isCreating ? (
            <div className="empty-state">
              <p>{searchTerm ? 'No projects match your search' : 'No folders yet. Create your first project to get started.'}</p>
            </div>
          ) : (
            <ul className="folder-tree-list">
              {filteredTree.map((node) => renderTreeNode(node))}
            </ul>
          )}
        </div>
      ) : (
        <div className="folder-list-flat">
          {/* Edit form shown above grid when editing */}
          {editingFolderId && (
            <form className="folder-form" onSubmit={(e) => handleUpdate(editingFolderId, e)}>
              <h3>Edit Project</h3>
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter project name"
                  disabled={isLoading}
                  required
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Enter description (optional)"
                  disabled={isLoading}
                  rows={3}
                />
              </div>
              <div className="form-group">
                <label>Project Icon</label>
                <div className="icon-upload-row">
                  {formData.icon && (
                    <img src={formData.icon} alt="icon preview" className="icon-preview" />
                  )}
                  <input type="file" accept="image/*" onChange={handleIconUpload} disabled={isLoading} />
                  {formData.icon && (
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => setFormData(prev => ({ ...prev, icon: '' }))}>
                      Remove
                    </button>
                  )}
                </div>
              </div>
              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={isLoading}>
                  {isLoading ? 'Saving...' : 'Save'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={cancelEdit} disabled={isLoading}>
                  Cancel
                </button>
              </div>
            </form>
          )}
          <div className="projects-grid">
            {filteredFolders.length === 0 && !isCreating ? (
              <div className="projects-empty">
                <div className="projects-empty-icon">📁</div>
                <p>{searchTerm ? 'No projects match your search' : 'No projects yet. Create your first project to get started.'}</p>
              </div>
            ) : (
              <>
                {filteredFolders.map((folder) => {
                  const color = getCardColor(folder.name);
                  const initial = folder.name.charAt(0).toUpperCase();
                  const authorInitial = emailToUsername(folder.created_by).charAt(0).toUpperCase();
                  return (
                    <div
                      key={folder.id}
                      className="project-card"
                      onClick={() => onFolderSelect?.(folder)}
                    >
                      {/* Card body */}
                      <div className="project-card-body">
                        <div className="project-card-top">
                          {/* Icon box */}
                          <div
                            className="project-icon-box"
                            style={{ background: color.bg, color: color.text }}
                          >
                            {folder.icon
                              ? <img src={folder.icon} alt={folder.name} className="project-avatar-img" />
                              : initial
                            }
                          </div>
                          {/* Action buttons — only for write roles */}
                          {isAdmin && (
                            <div className="project-card-actions" onClick={(e) => e.stopPropagation()}>
                              <button className="btn btn-icon" onClick={() => startEdit(folder)} disabled={isLoading} title="Edit project">
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
                                  <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
                                </svg>
                              </button>
                              <button className="btn btn-icon btn-icon-danger" onClick={() => handleDelete(folder.id)} disabled={isLoading} title="Delete project">
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
                                </svg>
                              </button>
                            </div>
                          )}
                        </div>
                        {/* Title + description */}
                        <div className="project-card-content">
                          <h3>{folder.name}</h3>
                          <p>{folder.description || 'No description provided.'}</p>
                        </div>
                      </div>
                      {/* Footer */}
                      <div className="project-card-footer">
                        <div className="project-card-author">
                          <div className="project-author-avatar" style={{ background: color.text }}>
                            {authorInitial}
                          </div>
                          <span className="project-author-name">{emailToUsername(folder.created_by)}</span>
                        </div>
                        <span className="project-card-date">{formatDate(folder.created_at)}</span>
                      </div>
                    </div>
                  );
                })}
                {/* Add new project placeholder — write roles only */}
                {!hideCreateButton && isAdmin && (
                  <div className="project-card-add" onClick={() => startCreate()}>
                    <div className="project-card-add-icon">+</div>
                    <p>Start New Project</p>
                    <span>Initialize a clean workspace</span>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
